import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { logStatusTriggeredMessage } from '../utils/communicationManager';
import { useUser, SignInButton } from '@clerk/react';
import {
    CheckCircle, ArrowLeft, ArrowRight, Upload, Camera, User, Phone, Mail,
    Calendar, MapPin, Lock, Car, FileText, Building, CreditCard,
    Shield, Heart, Star, ChevronDown, ChevronUp, AlertCircle, X, Check,
    Loader, Eye, EyeOff, Plus, Minus, Wifi, Baby, Zap, Coffee, Award
} from 'lucide-react';

// ─── Nigerian Data ───────────────────────────────────────────────────────────
const NIGERIAN_STATES = [
    'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
    'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT - Abuja','Gombe',
    'Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos',
    'Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto',
    'Taraba','Yobe','Zamfara'
];
const NIGERIAN_BANKS = [
    'Access Bank','First Bank','GTBank','Zenith Bank','UBA','Stanbic IBTC',
    'FCMB','Fidelity Bank','Keystone Bank','Opay','Kuda Bank','Moniepoint',
    'PalmPay','Carbon','VFD Microfinance Bank','Sterling Bank','Union Bank',
    'Heritage Bank','Wema Bank','Jaiz Bank','Providus Bank','Titan Trust Bank'
];
const LAGOS_LGAS = [
    'Agege','Ajeromi-Ifelodun','Alimosho','Amuwo-Odofin','Apapa','Badagry',
    'Epe','Eti-Osa','Ibeju-Lekki','Ifako-Ijaiye','Ikeja','Ikorodu',
    'Kosofe','Lagos Island','Lagos Mainland','Mushin','Ojo','Oshodi-Isolo',
    'Shomolu','Surulere'
];
const SERVICE_AREAS = [
    'Victoria Island','Ikoyi','Lekki Phase 1','Lekki Phase 2','Ajah','Sangotedo',
    'Ikeja GRA','Maryland','Ojota','Yaba','Surulere','Gbagada','Magodo',
    'Ikoyi','Apapa','Festac','Isolo','Agege','Egbeda','Oshodi','Airport Road',
    'Ogudu','Ketu','Ojodu Berger','Wuse','Maitama','Garki','Gwarinpa'
];
const VEHICLE_MAKES = ['Toyota','Honda','Hyundai','Kia','Mercedes-Benz','BMW','Ford','Chevrolet','Nissan','Volkswagen','Lexus','Infiniti','Mazda','Mitsubishi','Suzuki','Peugeot','Renault'];

// ─── Toast Component ─────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
    <div style={{
        position:'fixed',top:20,right:20,zIndex:9999,
        background: type==='success' ? '#166534' : type==='error' ? '#991b1b' : '#1e40af',
        color:'white',padding:'12px 20px',borderRadius:12,
        display:'flex',alignItems:'center',gap:10,
        boxShadow:'0 8px 32px rgba(0,0,0,0.2)',
        animation:'slideIn 0.3s ease',maxWidth:320,fontSize:14,fontWeight:500
    }}>
        {type==='success'?<CheckCircle size={18}/>:type==='error'?<AlertCircle size={18}/>:<AlertCircle size={18}/>}
        <span>{message}</span>
        <button onClick={onClose} style={{background:'none',border:'none',color:'white',cursor:'pointer',marginLeft:'auto'}}><X size={16}/></button>
    </div>
);

// ─── FileUpload Component ─────────────────────────────────────────────────────
const FileUpload = ({ label, hint, value, onChange, icon: Icon = Upload, accept = "image/*,.pdf" }) => {
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef();
    const handleFile = (file) => {
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => onChange({ name: file.name, url: e.target.result, size: file.size });
            reader.readAsDataURL(file);
        }
    };
    return (
        <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            style={{
                border: `2px dashed ${dragging ? '#16a34a' : value ? '#16a34a' : '#d1d5db'}`,
                borderRadius: 16, padding: '20px 16px', cursor: 'pointer',
                background: value ? '#f0fdf4' : dragging ? '#f0fdf4' : '#fafafa',
                textAlign: 'center', transition: 'all 0.2s', marginBottom: 4
            }}
        >
            <input ref={inputRef} type="file" accept={accept} style={{ display:'none' }}
                onChange={(e) => handleFile(e.target.files[0])} />
            {value ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'center' }}>
                    <CheckCircle size={20} color="#16a34a" />
                    <div style={{ textAlign:'left' }}>
                        <p style={{ fontSize:13, fontWeight:600, color:'#166534', margin:0 }}>{value.name}</p>
                        <p style={{ fontSize:11, color:'#6b7280', margin:0 }}>{(value.size/1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={(e)=>{e.stopPropagation();onChange(null);}} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'#ef4444'}}><X size={16}/></button>
                </div>
            ) : (
                <>
                    <Icon size={28} color="#9ca3af" style={{marginBottom:8}} />
                    <p style={{ fontSize:13, fontWeight:600, color:'#374151', margin:'0 0 4px' }}>{label}</p>
                    {hint && <p style={{ fontSize:11, color:'#9ca3af', margin:0 }}>{hint}</p>}
                </>
            )}
        </div>
    );
};

// ─── ToggleChip Component ─────────────────────────────────────────────────────
const ToggleChip = ({ label, selected, onClick, icon: Icon }) => (
    <button type="button" onClick={onClick} style={{
        padding: '10px 16px', borderRadius: 999, border: `2px solid ${selected ? '#16a34a' : '#e5e7eb'}`,
        background: selected ? '#f0fdf4' : 'white', color: selected ? '#166534' : '#6b7280',
        fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center',
        gap: 6, transition: 'all 0.2s', whiteSpace:'nowrap'
    }}>
        {Icon && <Icon size={14} />}
        {label}
        {selected && <Check size={12} />}
    </button>
);

// ─── SelectCard ───────────────────────────────────────────────────────────────
const SelectCard = ({ label, desc, selected, onClick, icon: Icon }) => (
    <button type="button" onClick={onClick} style={{
        width:'100%', padding:'16px', borderRadius:14,
        border: `2px solid ${selected ? '#16a34a' : '#e5e7eb'}`,
        background: selected ? '#f0fdf4' : 'white', textAlign:'left',
        cursor:'pointer', transition:'all 0.2s', display:'flex', alignItems:'center', gap:12
    }}>
        <div style={{
            width:40,height:40,borderRadius:10,
            background: selected ? '#16a34a' : '#f3f4f6',
            display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0
        }}>
            {Icon && <Icon size={18} color={selected ? 'white' : '#9ca3af'} />}
        </div>
        <div style={{flex:1}}>
            <p style={{ margin:0, fontWeight:700, fontSize:14, color: selected ? '#166534' : '#111827' }}>{label}</p>
            {desc && <p style={{ margin:0, fontSize:12, color:'#6b7280', marginTop:2 }}>{desc}</p>}
        </div>
        {selected && <Check size={18} color="#16a34a" />}
    </button>
);

// ─── Input Field ─────────────────────────────────────────────────────────────
const Field = ({ label, required, error, children }) => (
    <div style={{ marginBottom: 18 }}>
        <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            {label} {required && <span style={{color:'#ef4444'}}>*</span>}
        </label>
        {children}
        {error && <p style={{ margin:'4px 0 0', fontSize:11, color:'#ef4444', display:'flex', alignItems:'center', gap:4 }}><AlertCircle size={12}/>{error}</p>}
    </div>
);

const inputStyle = {
    width:'100%', padding:'14px 16px', borderRadius:12, border:'1.5px solid #e5e7eb',
    fontSize:14, fontWeight:500, background:'white', outline:'none', boxSizing:'border-box',
    transition:'border-color 0.2s', fontFamily:'inherit', color:'#111827'
};

const selectStyle = { ...inputStyle, appearance:'none', cursor:'pointer' };

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ProviderOnboarding = () => {
    const { user: clerkUser } = useUser();
    const navigate = useNavigate();
    const TOTAL_STEPS = 9;
    const [currentStep, setCurrentStep] = useState(1);
    const [toast, setToast] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState(() => {
        try {
            const saved = localStorage.getItem('ez_naija_driver_onboarding');
            return saved ? JSON.parse(saved) : {
                // Step 1
                fullName:'', phone:'', email:'', dob:'', gender:'', address:'', state:'', lga:'',
                password:'', confirmPassword:'', profilePhoto: null,
                // Step 2
                driveProfessionally:'', yearsExperience:'', licenseNumber:'', licenseExpiry:'', licensePhoto: null,
                // Step 3
                vehicleOwnerName:'', vehicleMake:'', vehicleModel:'', vehicleYear:'',
                vehicleColor:'', plateNumber:'', seatingCapacity:'', transmission:'', hasAC:'',
                vehicleCategory:'', vehicleFrontPhoto:null, vehicleSidePhoto:null,
                vehicleInteriorPhoto:null, vehicleRegistration:null,
                proofOfInsurance:null, roadWorthiness:null,
                // Step 4
                registrationType:'', businessName:'', cacNumber:'', tin:'', businessAddress:'',
                website:'', instagram:'', cacCertificate:null, meansOfId:null, companyLogo:null,
                // Step 5
                primaryCity:'Lagos', serviceAreas:[], airportPickup:'', preferredAirports:[], availability:[],
                // Step 6
                bankName:'', accountName:'', accountNumber:'', wallets:[],
                // Step 7
                priorConviction:'', emergencyName:'', emergencyPhone:'', guarantorName:'', guarantorPhone:'',
                // Step 8
                languages:[], amenities:[],
                // Step 9
                confirmAccurate:false, agreeTerms:false, consentVerification:false, agreeCommission:false
            };
        } catch { return {}; }
    });

    // Auto-save to localStorage
    useEffect(() => {
        const toSave = { ...formData, profilePhoto: null, licensePhoto: null, vehicleFrontPhoto:null,
            vehicleSidePhoto:null, vehicleInteriorPhoto:null, vehicleRegistration:null,
            proofOfInsurance:null, roadWorthiness:null, cacCertificate:null, meansOfId:null, companyLogo:null };
        localStorage.setItem('ez_naija_driver_onboarding', JSON.stringify(toSave));
    }, [formData]);

    const update = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const toggleArray = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(value) ? prev[field].filter(v => v !== value) : [...prev[field], value]
        }));
    };

    const showToast = (message, type = 'info') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const validateStep = (step) => {
        const newErrors = {};
        if (step === 1) {
            if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
            if (!formData.phone.match(/^(\+234|0)[789]\d{9}$/)) newErrors.phone = 'Enter a valid Nigerian phone number';
            if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = 'Enter a valid email address';
            if (!formData.dob) newErrors.dob = 'Date of birth is required';
            if (!formData.gender) newErrors.gender = 'Please select your gender';
            if (!formData.address.trim()) newErrors.address = 'Home address is required';
            if (!formData.state) newErrors.state = 'Please select your state';
            if (!formData.password || formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
            if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        }
        if (step === 2) {
            if (!formData.driveProfessionally) newErrors.driveProfessionally = 'Please answer this question';
            if (!formData.yearsExperience) newErrors.yearsExperience = 'Please select experience level';
            if (!formData.licenseNumber.trim()) newErrors.licenseNumber = 'License number is required';
            if (!formData.licenseExpiry) newErrors.licenseExpiry = 'License expiry is required';
        }
        if (step === 3) {
            if (!formData.vehicleOwnerName.trim()) newErrors.vehicleOwnerName = 'Vehicle owner name is required';
            if (!formData.vehicleMake) newErrors.vehicleMake = 'Please select vehicle make';
            if (!formData.vehicleModel.trim()) newErrors.vehicleModel = 'Vehicle model is required';
            if (!formData.vehicleYear) newErrors.vehicleYear = 'Vehicle year is required';
            if (!formData.plateNumber.trim()) newErrors.plateNumber = 'Plate number is required';
            if (!formData.seatingCapacity) newErrors.seatingCapacity = 'Seating capacity is required';
            if (!formData.transmission) newErrors.transmission = 'Please select transmission type';
            if (!formData.vehicleCategory) newErrors.vehicleCategory = 'Please select vehicle category';
        }
        if (step === 6) {
            if (!formData.bankName) newErrors.bankName = 'Please select your bank';
            if (!formData.accountName.trim()) newErrors.accountName = 'Account name is required';
            if (!formData.accountNumber.match(/^\d{10}$/)) newErrors.accountNumber = 'Enter a valid 10-digit account number';
        }
        if (step === 7) {
            if (!formData.priorConviction) newErrors.priorConviction = 'Please answer this question';
            if (!formData.emergencyName.trim()) newErrors.emergencyName = 'Emergency contact name is required';
            if (!formData.emergencyPhone.match(/^(\+234|0)[789]\d{9}$/)) newErrors.emergencyPhone = 'Enter a valid phone number';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (!validateStep(currentStep)) {
            showToast('Please fill in all required fields', 'error');
            return;
        }
        setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
        window.scrollTo(0, 0);
    };

    const handleBack = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        window.scrollTo(0, 0);
    };

    const handleSubmit = async () => {
        if (!formData.confirmAccurate || !formData.agreeTerms || !formData.consentVerification || !formData.agreeCommission) {
            showToast('Please confirm all checkboxes to proceed', 'error');
            return;
        }
        setIsSubmitting(true);

        const clerkUserId = clerkUser?.id || null;
        const providerEmail = formData.email || clerkUser?.primaryEmailAddress?.emailAddress || '';

        // Attempt 1: Schema 1 (supabase_schema.sql style)
        const payload1 = {
            id: clerkUserId, // Primary key references profiles.id
            full_name: formData.fullName,
            business_name: formData.businessName || null,
            email: providerEmail,
            phone_number: formData.phone,
            whatsapp_number: formData.phone,
            category: 'Driver',
            location_state: formData.state,
            location_city: formData.lga,
            experience_years: parseInt(formData.yearsExperience) || 0,
            cac_number: formData.cacNumber || null,
            id_type: formData.meansOfId || 'Driver License',
            bank_name: formData.bankName || null,
            bank_account_number: formData.accountNumber || null,
            bank_account_name: formData.accountName || null,
            availability: formData.availability || [],
            service_description: `Driver offering services in ${formData.state}`,
            social_links: { website: formData.website || '', instagram: formData.instagram || '' },
            emergency_contact: { name: formData.emergencyName || '', phone: formData.emergencyPhone || '' },
            dynamic_fields: {
                vehicleMake: formData.vehicleMake || '',
                vehicleModel: formData.vehicleModel || '',
                vehicleYear: formData.vehicleYear || '',
                vehicleColor: formData.vehicleColor || '',
                plateNumber: formData.plateNumber || '',
                seatingCapacity: formData.seatingCapacity || '',
                transmission: formData.transmission || '',
                hasAC: formData.hasAC === 'Yes' || formData.hasAC === true || false
            },
            status: 'submitted'
        };

        let dbProvider = null;
        let success = false;

        try {
            console.log("Attempting schema 1 registration in Supabase...");
            const { data, error } = await supabase
                .from('providers')
                .insert([payload1])
                .select();
            
            if (error) {
                // If it's a schema mismatch, throw to catch and try payload 2
                throw error;
            }
            if (data && data.length > 0) {
                dbProvider = data[0];
                success = true;
            }
        } catch (err) {
            console.warn("Schema 1 registration failed or mismatched. Trying schema 2...", err);
            
            // Attempt 2: Schema 2 (provider_schema.sql style)
            const generatedAppId = 'EZP-' + Math.floor(100000 + Math.random() * 900000);
            const payload2 = {
                id: clerkUserId || crypto.randomUUID(), // If ID is required and matches profiles
                application_id: generatedAppId,
                status: 'submitted',
                verification_level: 'Tier 1',
                is_live: false,
                full_name: formData.fullName,
                business_name: formData.businessName || null,
                email: providerEmail,
                phone: formData.phone,
                whatsapp: formData.phone,
                category: 'Driver',
                experience_years: parseInt(formData.yearsExperience) || 0,
                cac_number: formData.cacNumber || null,
                government_id_type: formData.meansOfId || 'Driver License',
                bank_name: formData.bankName || null,
                account_name: formData.accountName || null,
                account_number: formData.accountNumber || null,
                service_description: `Driver offering services in ${formData.state}`,
                portfolio_links: { website: formData.website || '', instagram: formData.instagram || '' },
                emergency_contact: { name: formData.emergencyName || '', phone: formData.emergencyPhone || '' },
                dynamic_fields: {
                    vehicleMake: formData.vehicleMake || '',
                    vehicleModel: formData.vehicleModel || '',
                    vehicleYear: formData.vehicleYear || '',
                    vehicleColor: formData.vehicleColor || '',
                    plateNumber: formData.plateNumber || '',
                    seatingCapacity: formData.seatingCapacity || '',
                    transmission: formData.transmission || '',
                    hasAC: formData.hasAC || false
                }
            };

            try {
                const { data, error } = await supabase
                    .from('providers')
                    .insert([payload2])
                    .select();
                if (error) throw error;
                if (data && data.length > 0) {
                    dbProvider = data[0];
                    success = true;
                }
            } catch (err2) {
                console.error("Schema 2 registration also failed.", err2);
            }
        }

        // Trigger Notification & fallback
        if (success && dbProvider) {
            console.log("Successfully created provider row. Triggering email...");
            try {
                // Try logging message triggered email
                await logStatusTriggeredMessage(supabase, dbProvider, 'Submitted');
            } catch (notifyErr) {
                console.error("Error triggering status notification:", notifyErr);
            }
        } else {
            console.warn("Database registration could not be completed. Falling back to local logs.");
            // Mock provider details for local storage notification fallback
            const mockProvider = {
                id: crypto.randomUUID(),
                application_id: 'EZP-' + Math.floor(100000 + Math.random() * 900000),
                full_name: formData.fullName,
                email: providerEmail,
                phone: formData.phone
            };
            try {
                await logStatusTriggeredMessage(supabase, mockProvider, 'Submitted');
            } catch (fallbackErr) {
                console.error("Failed notification fallback:", fallbackErr);
            }
        }

        setIsSubmitting(false);
        setSubmitted(true);
        localStorage.removeItem('ez_naija_driver_onboarding');
    };

    const progress = Math.round(((currentStep - 1) / (TOTAL_STEPS - 1)) * 100);

    const stepLabels = ['Basic Info','Driver Verify','Vehicle','Business','Coverage','Payouts','Safety','Experience','Submit'];

    // ─── Success Screen ───────────────────────────────────────────────────────
    if (submitted) {
        return (
            <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, background:'#f9fafb', textAlign:'center' }}>
                <div style={{ width:100,height:100,borderRadius:'50%',background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:24,boxShadow:'0 0 0 20px #dcfce7' }}>
                    <CheckCircle size={52} color="#16a34a" />
                </div>
                <h1 style={{ fontSize:26,fontWeight:800,color:'#111827',margin:'0 0 12px' }}>Application Submitted!</h1>
                <p style={{ fontSize:15,color:'#374151',maxWidth:320,margin:'0 0 8px',lineHeight:1.6 }}>
                    Your application has been submitted successfully.
                </p>
                <p style={{ fontSize:13,color:'#6b7280',maxWidth:300,margin:'0 0 36px',lineHeight:1.6 }}>
                    EZ Naija reviews all drivers to ensure safety, professionalism, and quality service. You will hear from us within 24–48 hours.
                </p>
                <div style={{ width:'100%',maxWidth:360,display:'flex',flexDirection:'column',gap:12 }}>
                    <button onClick={() => navigate('/provider-dashboard')} style={{ padding:'16px',borderRadius:14,background:'#15803d',color:'white',fontWeight:700,fontSize:15,border:'none',cursor:'pointer' }}>
                        Go to Dashboard
                    </button>
                    <button onClick={() => navigate('/provider-dashboard')} style={{ padding:'16px',borderRadius:14,background:'white',color:'#15803d',fontWeight:700,fontSize:15,border:'2px solid #16a34a',cursor:'pointer' }}>
                        Track Verification Status
                    </button>
                </div>
            </div>
        );
    }

    // ─── RENDER STEPS ─────────────────────────────────────────────────────────
    return (
        <div style={{ minHeight:'100vh', background:'#f9fafb', fontFamily:'"Inter", system-ui, sans-serif', paddingBottom:100 }}>
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            {/* Sticky Header */}
            <div style={{ position:'sticky',top:0,zIndex:100,background:'white',borderBottom:'1px solid #f3f4f6',boxShadow:'0 1px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ maxWidth:520,margin:'0 auto',padding:'12px 20px' }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10 }}>
                        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                            {currentStep > 1 && (
                                <button onClick={handleBack} style={{ background:'#f3f4f6',border:'none',borderRadius:8,padding:'6px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontWeight:600,fontSize:13,color:'#374151' }}>
                                    <ArrowLeft size={14}/> Back
                                </button>
                            )}
                            <div>
                                <p style={{ margin:0,fontSize:11,color:'#9ca3af',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em' }}>Step {currentStep} of {TOTAL_STEPS}</p>
                                <p style={{ margin:0,fontSize:13,fontWeight:700,color:'#111827' }}>{stepLabels[currentStep-1]}</p>
                            </div>
                        </div>
                        <div style={{ background:'#f0fdf4',padding:'4px 10px',borderRadius:999,fontSize:12,fontWeight:700,color:'#166534' }}>
                            {progress}% Complete
                        </div>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height:4,background:'#e5e7eb',borderRadius:999,overflow:'hidden' }}>
                        <div style={{ height:'100%',width:`${progress}%`,background:'linear-gradient(90deg,#15803d,#22c55e)',borderRadius:999,transition:'width 0.4s ease' }}/>
                    </div>
                    {/* Step dots */}
                    <div style={{ display:'flex',gap:4,marginTop:8,justifyContent:'center' }}>
                        {stepLabels.map((label, idx) => (
                            <div key={idx} title={label} style={{
                                height:4,flex:1,borderRadius:999,
                                background: idx < currentStep ? '#15803d' : '#e5e7eb',
                                transition:'background 0.3s'
                            }}/>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ maxWidth:520,margin:'0 auto',padding:'24px 20px' }}>

                {/* ── STEP 1: BASIC INFORMATION ── */}
                {currentStep === 1 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <User size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Let's get you onboarded</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280',lineHeight:1.5 }}>Join EZ Naija's network of verified providers.</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <Field label="Full Name" required error={errors.fullName}>
                                <input style={{...inputStyle, borderColor: errors.fullName?'#ef4444':'#e5e7eb'}} placeholder="e.g. Olawale Johnson" value={formData.fullName} onChange={e=>update('fullName',e.target.value)} />
                            </Field>
                            <Field label="Phone Number" required error={errors.phone}>
                                <input style={{...inputStyle, borderColor: errors.phone?'#ef4444':'#e5e7eb'}} placeholder="+234 801 234 5678" value={formData.phone} onChange={e=>update('phone',e.target.value)} />
                            </Field>
                            <Field label="Email Address" required error={errors.email}>
                                <input style={{...inputStyle, borderColor: errors.email?'#ef4444':'#e5e7eb'}} placeholder="you@email.com" type="email" value={formData.email} onChange={e=>update('email',e.target.value)} />
                            </Field>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                <Field label="Date of Birth" required error={errors.dob}>
                                    <input style={{...inputStyle, borderColor: errors.dob?'#ef4444':'#e5e7eb'}} type="date" value={formData.dob} onChange={e=>update('dob',e.target.value)} />
                                </Field>
                                <Field label="Gender" required error={errors.gender}>
                                    <select style={{...selectStyle, borderColor: errors.gender?'#ef4444':'#e5e7eb'}} value={formData.gender} onChange={e=>update('gender',e.target.value)}>
                                        <option value="">Select</option>
                                        <option>Male</option><option>Female</option><option>Prefer not to say</option>
                                    </select>
                                </Field>
                            </div>
                            <Field label="Home Address" required error={errors.address}>
                                <input style={{...inputStyle, borderColor: errors.address?'#ef4444':'#e5e7eb'}} placeholder="No. 1, Bourdillon Road, Ikoyi" value={formData.address} onChange={e=>update('address',e.target.value)} />
                            </Field>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                <Field label="State" required error={errors.state}>
                                    <select style={{...selectStyle, borderColor: errors.state?'#ef4444':'#e5e7eb'}} value={formData.state} onChange={e=>update('state',e.target.value)}>
                                        <option value="">Select State</option>
                                        {NIGERIAN_STATES.map(s=><option key={s}>{s}</option>)}
                                    </select>
                                </Field>
                                <Field label="LGA">
                                    <select style={selectStyle} value={formData.lga} onChange={e=>update('lga',e.target.value)}>
                                        <option value="">Select LGA</option>
                                        {LAGOS_LGAS.map(l=><option key={l}>{l}</option>)}
                                    </select>
                                </Field>
                            </div>
                            <Field label="Create Password" required error={errors.password}>
                                <div style={{ position:'relative' }}>
                                    <input style={{...inputStyle, paddingRight:44, borderColor: errors.password?'#ef4444':'#e5e7eb'}} type={showPassword?'text':'password'} placeholder="Min. 8 characters" value={formData.password} onChange={e=>update('password',e.target.value)} />
                                    <button type="button" onClick={()=>setShowPassword(p=>!p)} style={{ position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af' }}>
                                        {showPassword?<EyeOff size={18}/>:<Eye size={18}/>}
                                    </button>
                                </div>
                            </Field>
                            <Field label="Confirm Password" required error={errors.confirmPassword}>
                                <div style={{ position:'relative' }}>
                                    <input style={{...inputStyle, paddingRight:44, borderColor: errors.confirmPassword?'#ef4444':'#e5e7eb'}} type={showConfirmPassword?'text':'password'} placeholder="Confirm your password" value={formData.confirmPassword} onChange={e=>update('confirmPassword',e.target.value)} />
                                    <button type="button" onClick={()=>setShowConfirmPassword(p=>!p)} style={{ position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af' }}>
                                        {showConfirmPassword?<EyeOff size={18}/>:<Eye size={18}/>}
                                    </button>
                                </div>
                            </Field>
                            <Field label="Profile Photo / Selfie">
                                <FileUpload label="Tap to upload profile photo" hint="JPG, PNG • Max 5MB" value={formData.profilePhoto} onChange={v=>update('profilePhoto',v)} icon={Camera} accept="image/*" />
                            </Field>
                        </div>
                    </div>
                )}

                {/* ── STEP 2: DRIVER VERIFICATION ── */}
                {currentStep === 2 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <FileText size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Driver Verification</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>Help us verify your driving credentials.</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <Field label="Do you currently drive professionally?" required error={errors.driveProfessionally}>
                                <div style={{ display:'flex',gap:12 }}>
                                    <SelectCard label="Yes" desc="Full-time or part-time" selected={formData.driveProfessionally==='yes'} onClick={()=>update('driveProfessionally','yes')} icon={Check}/>
                                    <SelectCard label="No" desc="New to driving services" selected={formData.driveProfessionally==='no'} onClick={()=>update('driveProfessionally','no')} icon={X}/>
                                </div>
                            </Field>

                            <Field label="Years of Driving Experience" required error={errors.yearsExperience}>
                                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8 }}>
                                    {['0–1 years','2–5 years','5–10 years','10+ years'].map(opt=>(
                                        <button key={opt} type="button" onClick={()=>update('yearsExperience',opt)} style={{
                                            padding:'12px',borderRadius:12,border:`2px solid ${formData.yearsExperience===opt?'#16a34a':'#e5e7eb'}`,
                                            background:formData.yearsExperience===opt?'#f0fdf4':'white',
                                            fontWeight:600,fontSize:13,cursor:'pointer',
                                            color:formData.yearsExperience===opt?'#166534':'#374151',transition:'all 0.2s'
                                        }}>{opt}</button>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Driver's License Number" required error={errors.licenseNumber}>
                                <input style={{...inputStyle, borderColor: errors.licenseNumber?'#ef4444':'#e5e7eb'}} placeholder="e.g. ABC12345XYZ" value={formData.licenseNumber} onChange={e=>update('licenseNumber',e.target.value.toUpperCase())} />
                            </Field>
                            <Field label="License Expiry Date" required error={errors.licenseExpiry}>
                                <input style={{...inputStyle, borderColor: errors.licenseExpiry?'#ef4444':'#e5e7eb'}} type="date" value={formData.licenseExpiry} onChange={e=>update('licenseExpiry',e.target.value)} min={new Date().toISOString().split('T')[0]} />
                            </Field>
                            <Field label="Upload Driver's License">
                                <FileUpload label="Driver's License (Front & Back)" hint="Clear photo or PDF • Max 5MB" value={formData.licensePhoto} onChange={v=>update('licensePhoto',v)} accept="image/*,.pdf" />
                            </Field>

                            <div style={{ background:'#f0fdf4',borderRadius:12,padding:'12px 16px',display:'flex',gap:10,alignItems:'flex-start' }}>
                                <Shield size={16} color="#15803d" style={{flexShrink:0,marginTop:2}}/>
                                <p style={{ margin:0,fontSize:12,color:'#166534',lineHeight:1.5 }}>
                                    Your documents are encrypted with bank-grade security and only used for verification purposes.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 3: VEHICLE INFORMATION ── */}
                {currentStep === 3 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <Car size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Vehicle Details</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>Tell us about the vehicle you'll be driving with.</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <Field label="Vehicle Owner Name" required error={errors.vehicleOwnerName}>
                                <input style={{...inputStyle, borderColor: errors.vehicleOwnerName?'#ef4444':'#e5e7eb'}} placeholder="Name on vehicle papers" value={formData.vehicleOwnerName} onChange={e=>update('vehicleOwnerName',e.target.value)} />
                            </Field>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                <Field label="Vehicle Make" required error={errors.vehicleMake}>
                                    <select style={{...selectStyle, borderColor: errors.vehicleMake?'#ef4444':'#e5e7eb'}} value={formData.vehicleMake} onChange={e=>update('vehicleMake',e.target.value)}>
                                        <option value="">Select Make</option>
                                        {VEHICLE_MAKES.map(m=><option key={m}>{m}</option>)}
                                    </select>
                                </Field>
                                <Field label="Model" required error={errors.vehicleModel}>
                                    <input style={{...inputStyle, borderColor: errors.vehicleModel?'#ef4444':'#e5e7eb'}} placeholder="e.g. Camry, Accord" value={formData.vehicleModel} onChange={e=>update('vehicleModel',e.target.value)} />
                                </Field>
                            </div>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                <Field label="Year" required error={errors.vehicleYear}>
                                    <select style={{...selectStyle, borderColor: errors.vehicleYear?'#ef4444':'#e5e7eb'}} value={formData.vehicleYear} onChange={e=>update('vehicleYear',e.target.value)}>
                                        <option value="">Year</option>
                                        {Array.from({length:20},(_,i)=>new Date().getFullYear()-i).map(y=><option key={y}>{y}</option>)}
                                    </select>
                                </Field>
                                <Field label="Color">
                                    <input style={inputStyle} placeholder="e.g. Silver" value={formData.vehicleColor} onChange={e=>update('vehicleColor',e.target.value)} />
                                </Field>
                            </div>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                <Field label="Plate Number" required error={errors.plateNumber}>
                                    <input style={{...inputStyle, borderColor: errors.plateNumber?'#ef4444':'#e5e7eb'}} placeholder="e.g. LAG-452-XP" value={formData.plateNumber} onChange={e=>update('plateNumber',e.target.value.toUpperCase())} />
                                </Field>
                                <Field label="Seating Capacity" required error={errors.seatingCapacity}>
                                    <select style={{...selectStyle, borderColor: errors.seatingCapacity?'#ef4444':'#e5e7eb'}} value={formData.seatingCapacity} onChange={e=>update('seatingCapacity',e.target.value)}>
                                        <option value="">Seats</option>
                                        {['2','3','4','5','6','7','8','10+'].map(s=><option key={s}>{s}</option>)}
                                    </select>
                                </Field>
                            </div>

                            <Field label="Transmission" required error={errors.transmission}>
                                <div style={{ display:'flex',gap:12 }}>
                                    {['Automatic','Manual'].map(t=>(
                                        <SelectCard key={t} label={t} selected={formData.transmission===t} onClick={()=>update('transmission',t)} icon={Car}/>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Air Conditioning?">
                                <div style={{ display:'flex',gap:12 }}>
                                    {['Yes','No'].map(v=>(
                                        <SelectCard key={v} label={v} selected={formData.hasAC===v} onClick={()=>update('hasAC',v)} icon={v==='Yes'?Check:X}/>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Vehicle Category" required error={errors.vehicleCategory}>
                                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8 }}>
                                    {['Economy','Comfort','SUV','Executive','Van'].map(cat=>(
                                        <button key={cat} type="button" onClick={()=>update('vehicleCategory',cat)} style={{
                                            padding:'12px',borderRadius:12,border:`2px solid ${formData.vehicleCategory===cat?'#16a34a':'#e5e7eb'}`,
                                            background:formData.vehicleCategory===cat?'#f0fdf4':'white',
                                            fontWeight:600,fontSize:13,cursor:'pointer',
                                            color:formData.vehicleCategory===cat?'#166534':'#374151',transition:'all 0.2s'
                                        }}>{cat}</button>
                                    ))}
                                </div>
                            </Field>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ margin:'0 0 16px',fontSize:16,fontWeight:700,color:'#111827' }}>Vehicle Documents & Photos</h3>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
                                <FileUpload label="Front Photo" value={formData.vehicleFrontPhoto} onChange={v=>update('vehicleFrontPhoto',v)} icon={Camera} accept="image/*" hint="Clear exterior shot" />
                                <FileUpload label="Side Photo" value={formData.vehicleSidePhoto} onChange={v=>update('vehicleSidePhoto',v)} icon={Camera} accept="image/*" hint="Full side view" />
                            </div>
                            <FileUpload label="Interior Photos" hint="Dashboard + back seat" value={formData.vehicleInteriorPhoto} onChange={v=>update('vehicleInteriorPhoto',v)} icon={Camera} accept="image/*" />
                            <FileUpload label="Vehicle Registration" hint="PDF or Image" value={formData.vehicleRegistration} onChange={v=>update('vehicleRegistration',v)} accept="image/*,.pdf" />
                            <FileUpload label="Proof of Insurance" hint="Current policy document" value={formData.proofOfInsurance} onChange={v=>update('proofOfInsurance',v)} accept="image/*,.pdf" />
                            <FileUpload label="Road Worthiness Certificate" hint="Valid road worthiness cert" value={formData.roadWorthiness} onChange={v=>update('roadWorthiness',v)} accept="image/*,.pdf" />
                        </div>
                    </div>
                )}

                {/* ── STEP 4: BUSINESS / CAC ── */}
                {currentStep === 4 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <Building size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Business Information</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>How are you registering with EZ Naija?</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <Field label="Registration Type">
                                <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
                                    <SelectCard label="Individual Driver" desc="Operate as an independent driver" selected={formData.registrationType==='individual'} onClick={()=>update('registrationType','individual')} icon={User}/>
                                    <SelectCard label="Registered Business / Fleet Operator" desc="You have a registered company or fleet" selected={formData.registrationType==='business'} onClick={()=>update('registrationType','business')} icon={Building}/>
                                </div>
                            </Field>

                            {formData.registrationType === 'business' && (
                                <div style={{ borderTop:'1px solid #f3f4f6',paddingTop:20,marginTop:8 }}>
                                    <p style={{ fontSize:12,fontWeight:700,color:'#15803d',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:16 }}>Business Details</p>
                                    <Field label="Registered Business Name" required>
                                        <input style={inputStyle} placeholder="e.g. Olawale Logistics Ltd" value={formData.businessName} onChange={e=>update('businessName',e.target.value)} />
                                    </Field>
                                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                        <Field label="CAC Number" required>
                                            <input style={inputStyle} placeholder="RC123456" value={formData.cacNumber} onChange={e=>update('cacNumber',e.target.value)} />
                                        </Field>
                                        <Field label="TIN (Optional)">
                                            <input style={inputStyle} placeholder="Tax ID" value={formData.tin} onChange={e=>update('tin',e.target.value)} />
                                        </Field>
                                    </div>
                                    <Field label="Business Address">
                                        <input style={inputStyle} placeholder="Registered business address" value={formData.businessAddress} onChange={e=>update('businessAddress',e.target.value)} />
                                    </Field>
                                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                        <Field label="Website (Optional)">
                                            <input style={inputStyle} placeholder="www.yoursite.com" value={formData.website} onChange={e=>update('website',e.target.value)} />
                                        </Field>
                                        <Field label="Instagram (Optional)">
                                            <input style={inputStyle} placeholder="@yourhandle" value={formData.instagram} onChange={e=>update('instagram',e.target.value)} />
                                        </Field>
                                    </div>
                                    <Field label="CAC Certificate">
                                        <FileUpload label="Upload CAC Certificate" hint="PDF or Image" value={formData.cacCertificate} onChange={v=>update('cacCertificate',v)} accept="image/*,.pdf"/>
                                    </Field>
                                    <Field label="Means of Identification">
                                        <FileUpload label="ID Document" hint="NIN, Passport, or Voter's Card" value={formData.meansOfId} onChange={v=>update('meansOfId',v)} accept="image/*,.pdf"/>
                                    </Field>
                                    <Field label="Company Logo (Optional)">
                                        <FileUpload label="Upload Logo" hint="PNG or JPG • Min 200×200" value={formData.companyLogo} onChange={v=>update('companyLogo',v)} icon={Camera} accept="image/*"/>
                                    </Field>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── STEP 5: SERVICE COVERAGE ── */}
                {currentStep === 5 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <MapPin size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Where do you operate?</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>Select your daily concierge service coverage areas.</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <Field label="Primary Operating City">
                                <select style={selectStyle} value={formData.primaryCity} onChange={e=>update('primaryCity',e.target.value)}>
                                    <option value="">Select City</option>
                                    {['Lagos','Abuja','Port Harcourt','Kano','Ibadan','Benin City','Enugu','Calabar'].map(c=><option key={c}>{c}</option>)}
                                </select>
                            </Field>

                            <Field label="Service Areas (Daily Concierge)">
                                <p style={{ fontSize:12,color:'#6b7280',margin:'0 0 12px' }}>Select the areas you cover for daily driving services</p>
                                <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                                    {SERVICE_AREAS.map(area=>(
                                        <ToggleChip key={area} label={area} selected={formData.serviceAreas.includes(area)} onClick={()=>toggleArray('serviceAreas',area)}/>
                                    ))}
                                </div>
                            </Field>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ margin:'0 0 16px',fontSize:16,fontWeight:700 }}>Airport Pickup (Optional)</h3>
                            <Field label="Available for Airport Pickups?">
                                <div style={{ display:'flex',gap:12 }}>
                                    <SelectCard label="Yes" desc="I want airport rides" selected={formData.airportPickup==='yes'} onClick={()=>update('airportPickup','yes')} icon={Check}/>
                                    <SelectCard label="No" desc="Concierge only" selected={formData.airportPickup==='no'} onClick={()=>update('airportPickup','no')} icon={X}/>
                                </div>
                            </Field>

                            {formData.airportPickup === 'yes' && (
                                <>
                                    <Field label="Preferred Airports">
                                        <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                                            {['MMIA Lagos','Abuja Airport','Port Harcourt Airport','Enugu Airport','Other'].map(a=>(
                                                <ToggleChip key={a} label={a} selected={formData.preferredAirports.includes(a)} onClick={()=>toggleArray('preferredAirports',a)}/>
                                            ))}
                                        </div>
                                    </Field>
                                    <Field label="Availability">
                                        <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                                            {['Morning','Afternoon','Evening','Overnight','24/7'].map(slot=>(
                                                <ToggleChip key={slot} label={slot} selected={formData.availability.includes(slot)} onClick={()=>toggleArray('availability',slot)}/>
                                            ))}
                                        </div>
                                    </Field>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* ── STEP 6: BANKING & PAYOUTS ── */}
                {currentStep === 6 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <CreditCard size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Payout Information</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>How should we pay you after each trip?</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <div style={{ background:'#f0fdf4',borderRadius:12,padding:'12px 16px',display:'flex',gap:10,marginBottom:20,alignItems:'center' }}>
                                <Shield size={16} color="#15803d"/>
                                <p style={{ margin:0,fontSize:12,color:'#166534',fontWeight:600 }}>Your banking information is encrypted and secured.</p>
                            </div>
                            <Field label="Bank Name" required error={errors.bankName}>
                                <select style={{...selectStyle, borderColor: errors.bankName?'#ef4444':'#e5e7eb'}} value={formData.bankName} onChange={e=>update('bankName',e.target.value)}>
                                    <option value="">Select your bank</option>
                                    {NIGERIAN_BANKS.map(b=><option key={b}>{b}</option>)}
                                </select>
                            </Field>
                            <Field label="Account Number" required error={errors.accountNumber}>
                                <input style={{...inputStyle, borderColor: errors.accountNumber?'#ef4444':'#e5e7eb', letterSpacing:'0.1em'}} placeholder="10-digit account number" maxLength={10} value={formData.accountNumber} onChange={e=>update('accountNumber',e.target.value.replace(/\D/g,''))} />
                            </Field>
                            <Field label="Account Name" required error={errors.accountName}>
                                <div style={{ position:'relative' }}>
                                    <input style={{...inputStyle, borderColor: errors.accountName?'#ef4444':'#e5e7eb', paddingRight: formData.accountNumber.length===10?44:16 }} placeholder="Auto-verified account name" value={formData.accountName} onChange={e=>update('accountName',e.target.value)} />
                                    {formData.accountNumber.length === 10 && (
                                        <div style={{ position:'absolute',right:14,top:'50%',transform:'translateY(-50%)' }}>
                                            <CheckCircle size={18} color="#16a34a"/>
                                        </div>
                                    )}
                                </div>
                            </Field>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ margin:'0 0 6px',fontSize:16,fontWeight:700 }}>Additional Wallets (Optional)</h3>
                            <p style={{ margin:'0 0 16px',fontSize:13,color:'#6b7280' }}>Select wallets you'd like to receive payouts to</p>
                            <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                                {['Opay','PalmPay','Moniepoint','Kuda','Carbon'].map(w=>(
                                    <ToggleChip key={w} label={w} selected={formData.wallets.includes(w)} onClick={()=>toggleArray('wallets',w)}/>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 7: SAFETY & TRUST ── */}
                {currentStep === 7 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <Shield size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Safety & Emergency Info</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>We take the safety of our passengers very seriously.</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <Field label="Have you ever been convicted of a crime?" required error={errors.priorConviction}>
                                <div style={{ display:'flex',gap:12 }}>
                                    <SelectCard label="Yes" selected={formData.priorConviction==='yes'} onClick={()=>update('priorConviction','yes')} icon={AlertCircle}/>
                                    <SelectCard label="No" selected={formData.priorConviction==='no'} onClick={()=>update('priorConviction','no')} icon={Check}/>
                                </div>
                            </Field>
                            {formData.priorConviction === 'yes' && (
                                <div style={{ background:'#fef2f2',borderRadius:12,padding:'12px 16px',marginBottom:16 }}>
                                    <p style={{ margin:0,fontSize:12,color:'#991b1b' }}>⚠️ A prior conviction does not automatically disqualify you. Our team will review your application on a case-by-case basis.</p>
                                </div>
                            )}
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <h3 style={{ margin:'0 0 16px',fontSize:16,fontWeight:700,color:'#111827' }}>Emergency Contact</h3>
                            <Field label="Contact Name" required error={errors.emergencyName}>
                                <input style={{...inputStyle, borderColor: errors.emergencyName?'#ef4444':'#e5e7eb'}} placeholder="Full name" value={formData.emergencyName} onChange={e=>update('emergencyName',e.target.value)} />
                            </Field>
                            <Field label="Contact Phone Number" required error={errors.emergencyPhone}>
                                <input style={{...inputStyle, borderColor: errors.emergencyPhone?'#ef4444':'#e5e7eb'}} placeholder="+234 801 234 5678" value={formData.emergencyPhone} onChange={e=>update('emergencyPhone',e.target.value)} />
                            </Field>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ margin:'0 0 16px',fontSize:16,fontWeight:700,color:'#111827' }}>Guarantor (Optional)</h3>
                            <Field label="Guarantor Name">
                                <input style={inputStyle} placeholder="Full name" value={formData.guarantorName} onChange={e=>update('guarantorName',e.target.value)} />
                            </Field>
                            <Field label="Guarantor Phone">
                                <input style={inputStyle} placeholder="+234 801 234 5678" value={formData.guarantorPhone} onChange={e=>update('guarantorPhone',e.target.value)} />
                            </Field>
                        </div>
                    </div>
                )}

                {/* ── STEP 8: CUSTOMER EXPERIENCE ── */}
                {currentStep === 8 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <Star size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Service Experience</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>What do you offer your passengers?</p>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <Field label="Languages Spoken">
                                <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                                    {['English','Yoruba','Igbo','Hausa','Pidgin','French','Other'].map(lang=>(
                                        <ToggleChip key={lang} label={lang} selected={formData.languages.includes(lang)} onClick={()=>toggleArray('languages',lang)}/>
                                    ))}
                                </div>
                            </Field>
                        </div>

                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ margin:'0 0 8px',fontSize:16,fontWeight:700 }}>Amenities & Services Offered</h3>
                            <p style={{ margin:'0 0 16px',fontSize:13,color:'#6b7280' }}>Select everything you provide</p>
                            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                                {[
                                    { key:'Child Seat', icon:Baby, desc:'For families with infants' },
                                    { key:'WiFi', icon:Wifi, desc:'In-vehicle hotspot' },
                                    { key:'Water Bottle', icon:Coffee, desc:'Refreshments provided' },
                                    { key:'Phone Charger', icon:Zap, desc:'USB charging port' },
                                    { key:'Luxury Service', icon:Award, desc:'Premium experience' },
                                    { key:'Music Request', icon:Star, desc:'Passenger controls music' },
                                ].map(({ key, icon:Icon, desc })=>(
                                    <button key={key} type="button" onClick={()=>toggleArray('amenities',key)} style={{
                                        padding:'14px 12px',borderRadius:14,
                                        border:`2px solid ${formData.amenities.includes(key)?'#16a34a':'#e5e7eb'}`,
                                        background:formData.amenities.includes(key)?'#f0fdf4':'white',
                                        cursor:'pointer',textAlign:'left',transition:'all 0.2s',
                                        display:'flex',flexDirection:'column',gap:6
                                    }}>
                                        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between' }}>
                                            <Icon size={18} color={formData.amenities.includes(key)?'#16a34a':'#9ca3af'}/>
                                            {formData.amenities.includes(key) && <Check size={14} color="#16a34a"/>}
                                        </div>
                                        <p style={{ margin:0,fontSize:13,fontWeight:700,color:formData.amenities.includes(key)?'#166534':'#111827' }}>{key}</p>
                                        <p style={{ margin:0,fontSize:11,color:'#9ca3af' }}>{desc}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 9: REVIEW & SUBMIT ── */}
                {currentStep === 9 && (
                    <div>
                        <div style={{ marginBottom:28 }}>
                            <div style={{ width:48,height:48,borderRadius:14,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                                <CheckCircle size={24} color="#15803d"/>
                            </div>
                            <h1 style={{ margin:'0 0 6px',fontSize:24,fontWeight:800,color:'#111827' }}>Review & Submit</h1>
                            <p style={{ margin:0,fontSize:14,color:'#6b7280' }}>Almost there! Please review and confirm your application.</p>
                        </div>

                        {/* Summary Card */}
                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:16 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:20 }}>
                                <div style={{ width:56,height:56,borderRadius:14,background:'#f0fdf4',overflow:'hidden',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center' }}>
                                    {formData.profilePhoto?.url ? <img src={formData.profilePhoto.url} alt="Profile" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : <User size={28} color="#9ca3af"/>}
                                </div>
                                <div>
                                    <p style={{ margin:0,fontWeight:800,fontSize:16,color:'#111827' }}>{formData.fullName || '—'}</p>
                                    <p style={{ margin:0,fontSize:13,color:'#6b7280' }}>{formData.email}</p>
                                    <div style={{ display:'inline-flex',alignItems:'center',gap:4,background:'#fef3c7',padding:'2px 8px',borderRadius:999,marginTop:4 }}>
                                        <div style={{width:6,height:6,borderRadius:'50%',background:'#f59e0b'}}/>
                                        <span style={{ fontSize:10,fontWeight:700,color:'#92400e',textTransform:'uppercase' }}>Pending Review</span>
                                    </div>
                                </div>
                            </div>

                            {[
                                { label:'Phone', value: formData.phone },
                                { label:'State', value: formData.state },
                                { label:'Vehicle', value: `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}`.trim() || '—' },
                                { label:'Plate', value: formData.plateNumber || '—' },
                                { label:'Category', value: formData.vehicleCategory || '—' },
                                { label:'Experience', value: formData.yearsExperience || '—' },
                                { label:'Primary City', value: formData.primaryCity || '—' },
                                { label:'Service Areas', value: formData.serviceAreas.length ? `${formData.serviceAreas.length} areas selected` : '—' },
                                { label:'Bank', value: formData.bankName || '—' },
                                { label:'Airport Pickups', value: formData.airportPickup === 'yes' ? 'Yes' : formData.airportPickup === 'no' ? 'No' : '—' },
                                { label:'Amenities', value: formData.amenities.length ? formData.amenities.join(', ') : 'None selected' },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ display:'flex',justifyContent:'space-between',paddingBlock:8,borderBottom:'1px solid #f9fafb' }}>
                                    <span style={{ fontSize:13,color:'#6b7280',fontWeight:500 }}>{label}</span>
                                    <span style={{ fontSize:13,color:'#111827',fontWeight:600,textAlign:'right',maxWidth:'60%' }}>{value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Agreements */}
                        <div style={{ background:'white',borderRadius:20,padding:24,boxShadow:'0 1px 12px rgba(0,0,0,0.06)',marginBottom:24 }}>
                            <h3 style={{ margin:'0 0 16px',fontSize:16,fontWeight:700 }}>Declarations & Agreements</h3>
                            {[
                                { field:'confirmAccurate', label:'I confirm that all information provided is accurate and truthful' },
                                { field:'agreeTerms', label:'I agree to EZ Naija Terms & Conditions and Community Guidelines' },
                                { field:'consentVerification', label:'I consent to background checks and document verification' },
                                { field:'agreeCommission', label:"I agree to EZ Naija's platform commission structure" },
                            ].map(({ field, label }) => (
                                <label key={field} style={{ display:'flex',alignItems:'flex-start',gap:12,cursor:'pointer',marginBottom:16 }}>
                                    <div onClick={()=>update(field,!formData[field])} style={{
                                        width:22,height:22,borderRadius:6,border:`2px solid ${formData[field]?'#16a34a':'#d1d5db'}`,
                                        background:formData[field]?'#16a34a':'white',
                                        display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1,cursor:'pointer'
                                    }}>
                                        {formData[field] && <Check size={13} color="white"/>}
                                    </div>
                                    <span style={{ fontSize:13,color:'#374151',lineHeight:1.5 }}>{label}</span>
                                </label>
                            ))}
                        </div>

                        {!clerkUser && (
                            <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '12px', border: '1px solid #fca5a5', marginBottom: '20px', textAlign: 'center' }}>
                                <AlertCircle size={24} color="var(--emergency-red)" style={{ margin: '0 auto 10px' }} />
                                <h4 style={{ color: '#991b1b', marginBottom: '8px' }}>Sign In Required</h4>
                                <p style={{ fontSize: '13px', color: '#7f1d1d', marginBottom: '16px' }}>
                                    You must be logged in to submit your application to the database.
                                </p>
                                <SignInButton mode="modal">
                                    <button style={{
                                        background: 'var(--primary-green)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '20px',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: 'pointer'
                                    }}>
                                        Log In / Sign Up
                                    </button>
                                </SignInButton>
                            </div>
                        )}

                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || !clerkUser}
                            style={{
                                width:'100%',padding:'18px',borderRadius:16,
                                background: (isSubmitting || !clerkUser) ? '#d1d5db' : 'linear-gradient(135deg,#15803d,#16a34a)',
                                color:'white',fontWeight:800,fontSize:16,border:'none',cursor: (isSubmitting || !clerkUser)?'default':'pointer',
                                display:'flex',alignItems:'center',justifyContent:'center',gap:10,
                                boxShadow: (isSubmitting || !clerkUser) ? 'none' : '0 8px 24px rgba(21,128,61,0.3)',
                                transition:'all 0.2s'
                            }}
                        >
                            {isSubmitting ? <><Loader size={20} style={{animation:'spin 1s linear infinite'}}/> Submitting...</> : <><CheckCircle size={20}/> Submit for Verification</>}
                        </button>
                    </div>
                )}

                {/* Navigation Buttons (Steps 1–8) */}
                {currentStep < 9 && (
                    <div style={{ marginTop:24 }}>
                        <button onClick={handleNext} style={{
                            width:'100%',padding:'16px',borderRadius:14,
                            background:'linear-gradient(135deg,#15803d,#16a34a)',
                            color:'white',fontWeight:700,fontSize:15,border:'none',cursor:'pointer',
                            display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                            boxShadow:'0 8px 24px rgba(21,128,61,0.25)'
                        }}>
                            Continue <ArrowRight size={18}/>
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                input:focus, select:focus { border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,0.1); }
            `}</style>
        </div>
    );
};

export default ProviderOnboarding;
